import { UserProfileIcon } from '@components/avatars';
import { IcoChat, IcoClock, IcoPinned } from '@components/icons';
import { useRouteParams } from '@components/router';
import { useCurrentTenant } from '@components/router/session-context';
import { genericDiscussionCategoryIds } from 'shared/consts';
import { timeago, fmtFullTime } from 'shared/dateutil';
import { useIntl } from 'shared/intl/use-intl';
import { emptyLessonTitle } from 'shared/terminology';
import { URLS } from 'shared/urls';
import { State } from './index';

interface Props {
  course: State['course'];
  discussion: State['feed']['discussions'][0];
}

function StartText({ discussion, course }: Pick<Props, 'discussion' | 'course'>) {
  const tenant = useCurrentTenant();
  const intl = useIntl();
  const { lesson } = discussion;
  const { categoryId } = useRouteParams();

  const [prefix, startLesson, suffix] =
    categoryId === genericDiscussionCategoryIds.lessons
      ? intl.split('Started in <>{lesson:string}</> lesson', {
          lesson: lesson?.title || emptyLessonTitle(tenant),
        })
      : intl.split('Started by <>{author:string}</>', {
          author: discussion.author.name,
        });

  return (
    <span class="line-clamp-1 leading-tight md:max-w-60">
      {prefix}
      <a
        class="capitalize"
        href={
          lesson
            ? URLS.student.lesson({ course, lesson })
            : URLS.student.memberProfile({
                course,
                user: discussion.author,
              })
        }
      >
        {startLesson}
      </a>
      {suffix} {timeago(discussion.createdAt, ' ago')}
    </span>
  );
}

export function ListItem({ course, discussion }: Props) {
  const { categoryId } = useRouteParams();
  const intl = useIntl();
  const isScheduled = discussion.scheduledAt && new Date(discussion.scheduledAt) > new Date();

  return (
    <a
      href={URLS.student.discussion({
        course,
        categoryId: categoryId || genericDiscussionCategoryIds.all,
        discussionId: discussion.id,
      })}
    >
      <article
        class={`flex items-center justify-between p-4 text-gray-800 dark:text-gray-200 rounded-lg mb-4 border dark:border-none relative ${
          isScheduled ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 dark:bg-gray-800'
        }`}
      >
        {discussion.isPinned && <IcoPinned class="absolute left-1 top-1 w-4 h-4 text-indigo-500" />}
        <div class="flex items-center justify-center mr-2 md:mr-4">
          <UserProfileIcon user={discussion.author} size="w-10 h-10" />
        </div>
        <div class="min-w-0 flex-1 mr-4">
          <div class="flex items-center gap-2">
            <span class="text-base font-semibold leading-6 truncate">{discussion.prompt}</span>
            {isScheduled && (
              <span class="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                <IcoClock class="w-3 h-3" />
                {fmtFullTime(new Date(discussion.scheduledAt!))}
              </span>
            )}
          </div>
          <span class="flex items-center gap-2 text-xs dark:text-gray-400 leading-3">
            <span class="hidden md:inline-flex">
              <StartText discussion={discussion} course={course} />
            </span>
            <Dot />
            {discussion.lastComment && (
              <>
                <span>
                  {intl('Last reply {timeago:string} by', {
                    timeago: timeago(discussion.lastComment.createdAt, ' ago')!,
                  })}{' '}
                  {discussion.lastComment.userName}
                </span>
                <Dot />
              </>
            )}
            <span class="inline-flex items-center">
              <IcoChat />
              <span class="ml-1">{discussion.numComments || 0}</span>
            </span>
          </span>
        </div>
      </article>
    </a>
  );
}

/**
 * Small '.' divider for spacing out text chunks in a single block
 */
export function Dot() {
  return <span class="leading-none hidden md:inline text-lg opacity-75">·</span>;
}
